import React from "react";
import { GraduationCap, LineChart, ArrowUpRight, ShieldAlert, Check } from "lucide-react";
import { Reveal, Overline, Stagger, StaggerItem } from "./Reveal";
import { EDUCATION_POINTS, RESEARCH_POINTS, INSIGHT_CATEGORIES, IMAGES } from "../lib/content";

const Pillar = ({ icon: Icon, title, points, delay }) => (
  <Reveal delay={delay} className="border border-white/10 bg-midnight p-8 lg:p-10">
    <div className="flex items-center gap-4">
      <span className="grid place-items-center w-12 h-12 border border-gold/40 text-gold rounded-full">
        <Icon size={20} />
      </span>
      <h3 className="font-serif text-3xl text-ivory">{title}</h3>
    </div>
    <ul className="mt-8 space-y-3">
      {points.map((p) => (
        <li key={p} className="flex items-start gap-3 text-ivory/65">
          <Check size={16} className="text-gold mt-1 shrink-0" />
          <span>{p}</span>
        </li>
      ))}
    </ul>
  </Reveal>
);

export const FinMartSection = () => (
  <section data-testid="finmart-section" className="bg-navy text-ivory py-24 lg:py-32 noise-overlay">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-12 items-end">
        <Reveal className="lg:col-span-7">
          <Overline>Chapter 03 — KRV FinMart</Overline>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight">KRV FinMart</h2>
          <p className="mt-3 text-gold text-sm uppercase tracking-[0.15em]">Structured Market Education & Research</p>
          <p className="mt-6 text-lg text-ivory/60 leading-relaxed max-w-2xl">
            KRV FinMart is being developed as an institutional financial education and research platform for individuals who want to understand markets through structured frameworks, disciplined processes, and evidence-based analysis.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <span className="text-sm border border-white/15 px-4 py-2 text-ivory/70">Primary — Working Professionals</span>
            <span className="text-sm border border-white/15 px-4 py-2 text-ivory/70">Foundation — Students & Beginners</span>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="overflow-hidden border border-white/10">
            <img src={IMAGES.research} alt="Systematic market research and data analysis" className="w-full h-72 object-cover" />
          </div>
        </Reveal>
      </div>

      {/* Two pillars */}
      <div className="mt-16 grid md:grid-cols-2 gap-8">
        <Pillar icon={GraduationCap} title="Education" points={EDUCATION_POINTS} delay={0} />
        <Pillar icon={LineChart} title="Research" points={RESEARCH_POINTS} delay={0.1} />
      </div>
      <Reveal className="mt-6 text-center text-ivory/40 text-sm">
        The research desk will eventually combine <span className="text-gold">Index + Equity</span>.
      </Reveal>

      {/* Weekly insights */}
      <div className="mt-24 border border-white/10 bg-midnight p-8 lg:p-12">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline>Public Research</Overline>
              <h3 className="font-serif font-light text-3xl sm:text-4xl tracking-tight">KRV FinMart Weekly Market Insights</h3>
              <p className="mt-5 text-ivory/60 leading-relaxed max-w-xl">
                KRV FinMart intends to publish regular public educational market insights to help readers understand market structure, risk, volatility, and broader market context.
              </p>
              <a href="#contact" data-testid="insights-cta" className="mt-8 inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-medium hover:bg-gold-muted transition-colors group">
                Read Market Insights
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Stagger className="space-y-3">
              {INSIGHT_CATEGORIES.map((c) => (
                <StaggerItem key={c} className="flex items-center justify-between border-b border-white/10 pb-3 text-ivory/75 hover:text-gold transition-colors">
                  <span>{c}</span>
                  <ArrowUpRight size={16} className="text-gold" />
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <Reveal className="mt-12 border border-gold/25 bg-gold/5 p-8 flex flex-col sm:flex-row gap-5">
        <ShieldAlert size={28} className="text-gold shrink-0" />
        <div>
          <p className="text-gold font-medium uppercase tracking-[0.15em] text-sm">Disclaimer</p>
          <p className="mt-3 text-sm text-ivory/60 leading-relaxed">
            KRV FinMart provides educational content and market-related information for learning and informational purposes. Nothing published by KRV FinMart should be interpreted as a promise or guarantee of returns, or as personalized investment advice. Market participation involves risk. Users should evaluate their own circumstances and seek appropriate professional advice where necessary.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
